import { useCurrentUser } from "@client/hooks/use-current-user";
import {
  isIPadOffline,
  OFFLINE_CACHE_KEYS,
  readOfflineCache,
} from "@client/offline/offlineCache";
import { resolveOfflineUser } from "@client/offline/resolveOfflineUser";
import { type ReactNode } from "react";
import { Login } from ".";
import { OfflineSignInGate } from "./OfflineSignInGate";
import { CompleteProfile } from "./CompleteProfile";

type CurrentUser = NonNullable<ReturnType<typeof useCurrentUser>["data"]>;

export function AuthWrapper({ children }: { children: ReactNode }) {
  const { data: user, isLoading } = useCurrentUser();

  if (isLoading) {
    return null;
  }

  // On the iPad app while offline, fall back to the user cached during a
  // previous online session instead of bouncing to the login screen. On
  // desktop/iPhone this resolves to the normal login behavior.
  const resolution = resolveOfflineUser<CurrentUser>({
    user,
    cachedUser: readOfflineCache<CurrentUser>(OFFLINE_CACHE_KEYS.currentUser),
    iPadOffline: isIPadOffline(),
  });

  if (resolution.kind === "offline-first-launch") {
    return <OfflineSignInGate />;
  }

  if (resolution.kind === "login") {
    return <Login />;
  }

  if (!resolution.user.userName) {
    return <CompleteProfile />;
  }

  return children;
}
